//@ts-nocheck
import Router, { routingMixin } from "@jack-henry/web-component-router";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { postRequest } from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import { routerConfig } from "../../routers";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-text-input/jha-form-text-input";
import "@banno/jha-wc/src/forms/jha-form-textarea-input/jha-form-textarea-input";
import { decryptValue, showToastMessage } from "../../utils/app-utils";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { AddBusinessUnitType } from "../../types/BusinessUnits/AddBusinessUnitType";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import Cookies from "js-cookie";

@customElement("add-business-unit")
export class AddBusinessUnit extends routingMixin(LitElement) {
  static get is() {
    return "add-business-unit";
  }

  @property({ type: Object }) formData: AddBusinessUnitType = {
    businessUnitName: "",
    description: "",
    status: "active",
    createdBy: decryptValue(Cookies.get("userId") || ""),
  };

  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) isEditable = /readonly/i.test(
    decryptValue(Cookies.get("userRole") || "")
  );

  static readonly styles = css`
    #btnCon {
      display: flex;
      flex-direction: row;
      gap: 15px;
    }
    #formRow {
      display: inline-block;
      flex-direction: column;
      max-width: 300px;
      margin-top: 20px;
      margin-bottom: 20px;
    }
    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }
    h2 {
      margin-bottom: 18px;
    }
    #addBusinessUnit {
      padding: 24px;
      height: -webkit-fill-available;
      display: flex;
      flex-direction: column;
    }
    jha-card {
      padding: 24px;
      width: -webkit-fill-available;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    jha-form-textarea-input {
      max-width: 410px;
    }
    jha-form-text-input {
      max-width: 410px;
    }
    jha-form-floating-group textarea {
      padding: 15px;
    }
  `;

  private liveInputValue(autoId: string, fallback: unknown): string {
    const host = this.shadowRoot?.querySelector(`[data-auto-id='${autoId}']`) as
      | (HTMLElement & { value?: unknown })
      | null
      | undefined;
    if (!host) return String(fallback ?? "");
    const inner = host.shadowRoot?.querySelector("input, textarea") as
      | HTMLInputElement
      | HTMLTextAreaElement
      | null
      | undefined;
    if (inner && typeof inner.value === "string") return inner.value;
    if (typeof host.value === "string") return host.value;
    return String(fallback ?? "");
  }

  businessUnitDataChange() {
    const businessUnitNameInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='businessUnitNameInput']"
    ) as HTMLInputElement | null;
    const businessUnitDescriptionInput = this.shadowRoot?.querySelector(
      "jha-form-textarea-input[data-auto-id='businessUnitDescriptionInput']"
    ) as HTMLTextAreaElement | null;

    if (businessUnitNameInput) {
      this.formData.businessUnitName = businessUnitNameInput.value;
    }
    if (businessUnitDescriptionInput) {
      this.formData.description = businessUnitDescriptionInput.value;
    }

    const addBusinessUnitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='addNewBusinessUnitButton']"
    ) as HTMLButtonElement | null;

    if (
      String(businessUnitNameInput?.value ?? "").trim() !== "" &&
      String(businessUnitDescriptionInput?.value ?? "").trim() !== ""
    ) {
      addBusinessUnitButton?.removeAttribute("disabled");
    } else {
      addBusinessUnitButton?.setAttribute("disabled", "");
    }
  }

  clearForm() {
    const businessUnitNameInput = document
      .querySelector("add-business-unit")
      ?.shadowRoot?.querySelector(
        "jha-form-text-input[data-auto-id='businessUnitNameInput']"
      ) as HTMLInputElement;
    const businessUnitDescriptionInput = document
      .querySelector("add-business-unit")
      ?.shadowRoot?.querySelector(
        "jha-form-textarea-input[data-auto-id='businessUnitDescriptionInput']"
      ) as HTMLTextAreaElement;

    if (businessUnitNameInput) {
      businessUnitNameInput.value = "";
    }

    if (businessUnitDescriptionInput) {
      businessUnitDescriptionInput.value = "";
    }
  }

  clearButtonLoading() {
    const addNewBusinessUnitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='addNewBusinessUnitButton']"
    ) as HTMLButtonElement | null;
    addNewBusinessUnitButton?.removeAttribute("isLoading");
    const cancelNewBusinessUnitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='cancelNewBusinessUnitButton']"
    ) as HTMLButtonElement | null;
    cancelNewBusinessUnitButton?.removeAttribute("isLoading");
  }

  isFormDirty(): boolean {
    // Read the live controls — @change has not fired yet if Cancel is clicked
    // while the field still has focus.
    const name = this.liveInputValue(
      "businessUnitNameInput",
      this.formData?.businessUnitName,
    ).trim();
    const description = this.liveInputValue(
      "businessUnitDescriptionInput",
      this.formData?.description,
    ).trim();
    return name !== "" || description !== "";
  }

  async onCancelClick() {
    const leave = () => {
      new Router(routerConfig as any).go("/business-units");
    };

    // isEditable is really "is read-only" here. Add starts empty, so any typed
    // name or description is a real change. No change → leave silently.
    if (this.isEditable || !this.isFormDirty()) {
      leave();
      return;
    }

    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      if (result === "confirm") {
        leave();
        return;
      }
    } finally {
      if (this.isConnected) {
        this.clearButtonLoading();
        this.apiLoading = false;
      }
    }
  }

  async onAddBusinessUnitClick() {
    this.apiLoading = true;
    const trimmedFormData = this.trimFormDataValues(this.formData);
    this.formData = trimmedFormData;

    if (this.validateFormData(this.formData)) {
      const formDataSubmit = await postRequest<AddBusinessUnitType>(
        Apis.ADD_BUSINESSUNIT,
        this.formData
      );
      if (formDataSubmit?.messageStatus === null) {
        this.clearButtonLoading();
        this.apiLoading = false;
        showToastMessage(
          "A new business unit has been created successfully.",
          "success"
        );
        setTimeout(() => {
          this.clearForm();
          new Router(routerConfig as any).go("/business-units");
        }, 1000);
      } else if (formDataSubmit.details.length > 0) {
        this.apiLoading = false;
        this.clearButtonLoading();
        const errMsg = getErrorMessage(formDataSubmit.details);
        showToastMessage(errMsg, "danger");
      } else {
        this.apiLoading = false;
        this.clearButtonLoading();
        showToastMessage("Something went wrong, please try again.", "danger");
      }
    }
    this.apiLoading = false;
  }

  validateFormData(formData: AddBusinessUnitType): boolean {
    if (formData.businessUnitName === "") {
      showToastMessage("Business Unit is a required field.", "danger");
      return false;
    }
    if (formData.description === "") {
      showToastMessage("Description is a required field.", "danger");
      return false;
    }
    return true;
  }

  trimFormDataValues(formData: AddBusinessUnitType): AddBusinessUnitType {
    const trimmedFormData: AddBusinessUnitType = {
      businessUnitName: formData.businessUnitName.trim(),
      description: formData.description.trim(),
      status: formData.status.trim(),
      createdBy: formData.createdBy,
    };
    return trimmedFormData;
  }

  render() {
    return html`
      <div id="addBusinessUnit">
        <h1>Add Business Unit</h1>
      
        <div>
          <h2>Business Unit Information</h2>
          <div style="max-width: 410px;">
            <jha-form-row>
              <jha-tooltip right text="Enter name of the business unit. (Maximum 256 characters)">
                <jha-form-text-input data-auto-id="businessUnitNameInput" label="Business Unit Name" required
                  warning="Business Unit Name is a required field." outline minlength="1" maxlength="256"
                  @input=${this.businessUnitDataChange} @change=${this.businessUnitDataChange}>
                </jha-form-text-input>
              </jha-tooltip>
            </jha-form-row>
          </div>
        </div>
        <div>
          <div style="max-width: 410px;">
            <jha-form-row>
              <jha-tooltip right text="Enter description of the business unit. (Maximum 256 characters)">
                <jha-form-textarea-input data-auto-id="businessUnitDescriptionInput" label="Description" role="textbox"
                  minlength="1" maxlength="256" required warning="Business Unit Description is a required field." type="text"
                  @input=${this.businessUnitDataChange} @change=${this.businessUnitDataChange}>
                </jha-form-textarea-input>
              </jha-tooltip>
            </jha-form-row>
          </div>
        </div>
      
        <div style="flex:1;"></div>
        <div id="btnCon">
          <jha-tooltip top text="Click to add new business unit.">
            <jha-button data-auto-id="addNewBusinessUnitButton" disabled type="button" large
              @click=${this.onAddBusinessUnitClick}>
              Add Business Unit
            </jha-button>
          </jha-tooltip>
      
          <jha-tooltip top text="Click to cancel adding the new business unit.">
            <jha-button data-auto-id="cancelNewBusinessUnitButton" type="button" tertiary large @click=${this.onCancelClick}>
              Cancel
            </jha-button>
          </jha-tooltip>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-business-unit": AddBusinessUnit;
  }
}
